import { faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { OptionListItem } from '@/types/optionDiscovery';
import { usePrices } from '@hooks/usePrices';
import { calculateOptionPriceAtPoint } from '@services/optionDiscoveryService';
import { volatilityService } from '@services/volatilityService';
import { brandColors } from '@utils/color-utils';
import { calculateLambda } from '@utils/leverage';

interface OptionListItemRowProps {
  option: OptionListItem;
  onClick: () => void;
  onBuy: () => void;
  index: number;
}

export function OptionListItemRow({
  option,
  onClick,
  onBuy,
  index,
}: OptionListItemRowProps) {
  const isCall = option.type === 'CALL';
  const isPositive = option.priceChangePercent >= 0;

  // Get live spot price for real-time calculations
  const pricesQuery = usePrices();
  const livePrices = pricesQuery.data;
  const liveSpotPrice = livePrices?.get(option.asset)?.price;

  // Calculate real-time option price and spot price
  const { realTimeSpotPrice, realTimeOptionPrice, volatility } = useMemo(() => {
    // Use live spot price if available, otherwise fall back to option.spotPrice
    const currentSpotPrice = liveSpotPrice ?? option.spotPrice;

    // Get volatility from service (should match what's used in the modal)
    const currentVolatility = volatilityService.getVolatility(option.asset);
    if (!currentVolatility || currentVolatility <= 0) {
      return {
        realTimeSpotPrice: currentSpotPrice,
        realTimeOptionPrice: option.currentPrice, // Fallback to cached price
        volatility: null as number | null,
      };
    }

    // Calculate option price in real-time using current spot price and volatility
    const now = Date.now();
    const realTimePrice = calculateOptionPriceAtPoint(
      option,
      currentSpotPrice,
      currentVolatility,
      now,
    );

    return {
      realTimeSpotPrice: currentSpotPrice,
      realTimeOptionPrice: realTimePrice > 0 ? realTimePrice : option.currentPrice,
      volatility: currentVolatility,
    };
  }, [option, liveSpotPrice]);

  const formatPrice = (price: number): string => {
    if (price >= 1) {
      return `$${price.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return `$${price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })}`;
  };

  const formatPercent = (percent: number): string => {
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}%`;
  };

  const formatStrikePrice = (strikePrice: number): string => {
    return strikePrice.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const calculateLeverage = (option: OptionListItem): number => {
    const lambda = calculateLambda({
      option,
      spotPrice: realTimeSpotPrice,
      optionPrice: realTimeOptionPrice,
      volatility: volatility ?? undefined,
    });
    return lambda ?? 0;
  };

  const formatLeverage = (leverage: number): string => {
    return `${leverage.toFixed(2)}x`;
  };

  const buttonColor = isCall ? brandColors.green : brandColors.red;

  const handleBuyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onBuy();
  };

  // Calculate moneyness (how close strike is to spot price)
  // Higher moneyness = closer to ATM = more traders
  const moneyness = useMemo(() => {
    // Use real-time spot price if available, otherwise fall back to option's spot price
    const spotPrice = realTimeSpotPrice ?? option.spotPrice;
    if (!spotPrice || spotPrice <= 0 || option.strikePrice <= 0) {
      return 0.01; // Default to very low moneyness (deep OTM) if we can't calculate
    }
    
    // Calculate percentage distance from spot price
    // For PUT: (spot - strike) / spot, for CALL: (strike - spot) / spot
    const percentAway = Math.abs((option.strikePrice - spotPrice) / spotPrice);
    
    // Convert percentage distance to moneyness (0-1 scale)
    // 0% away (ATM) = 1.0, 10% away ≈ 0.5, 15%+ away < 0.35
    // Use exponential decay: moneyness = e^(-percentAway * 7)
    // This ensures 10% OTM ≈ 0.50, 15% OTM ≈ 0.35, 20% OTM ≈ 0.25
    const moneynessValue = Math.exp(-percentAway * 7);
    
    return moneynessValue;
  }, [realTimeSpotPrice, option.spotPrice, option.strikePrice]);

  // Initialize base trader count based on moneyness
  // ATM options get higher base counts (10-500), OTM get lower (1-10)
  const baseTraderCount = useMemo(() => {
    const seed = `${option.asset}-${option.strikePrice}-${option.type}-${option.expiryTimestamp}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    // Use moneyness to scale the base count
    // ATM (moneyness ~1): 10-500 range
    // OTM (moneyness ~0): 1-10 range
    const seedValue = Math.abs(hash % 1000);
    let scaledCount: number;
    
    // With percentage-based calculation and factor 7:
    // 10% OTM ≈ 0.50, 11% OTM ≈ 0.46, 15% OTM ≈ 0.35
    // Set threshold at 0.51 to catch options that are 10%+ OTM as "deep OTM"
    // This ensures the most OTM options (10%+ away) get 1-10 range
    if (moneyness < 0.51) {
      // Deep OTM: use 1-10 range
      scaledCount = Math.floor((seedValue / 1000) * 9) + 1; // 1-10
    } else {
      // Medium to ATM: interpolate between 10 and 500
      // At moneyness 0.51: ~10, at moneyness 1.0: ~500
      const minCount = 10;
      const maxCount = 500;
      // Normalize moneyness from [0.51, 1.0] to [0, 1] for interpolation
      const normalizedMoneyness = Math.min(1, Math.max(0, (moneyness - 0.51) / 0.49));
      const range = minCount + (normalizedMoneyness * (maxCount - minCount)); // 10-500
      scaledCount = Math.floor((seedValue / 1000) * (range - minCount)) + minCount; // 10-500
    }
    
    return Math.max(1, Math.min(scaledCount, 999));
  }, [option.asset, option.strikePrice, option.type, option.expiryTimestamp, moneyness]);

  // State to track current trader count (can go up or down)
  const [traderCount, setTraderCount] = useState(baseTraderCount);
  
  // Track color state for animations
  const [traderCountColor, setTraderCountColor] = useState('rgba(255, 255, 255, 0.6)'); // Default grey
  const [shouldAnimate, setShouldAnimate] = useState(false); // Control when animation starts
  const colorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastColorChangeRef = useRef<number>(0);

  // Change frequency and amount based on moneyness
  // All intervals are 3 seconds, but change amounts and probabilities vary by moneyness
  useEffect(() => {
    // Calculate interval and change range based on moneyness
    const intervalMs = 3000; // All options update every 3 seconds
    
    const maxChange = moneyness > 0.7
      ? 30    // ATM: up to +30
      : moneyness > 0.3
      ? 20    // Medium: up to +20
      : 10;   // Deep OTM: up to +10
    
    const minChange = moneyness > 0.7
      ? -3    // ATM: can go down by 3
      : moneyness > 0.3
      ? -2    // Medium: can go down by 2
      : -1;   // Deep OTM: can go down by 1
    
    // Probability of change also depends on moneyness
    // ATM: 10% chance, Medium: 7% chance, Deep OTM: 4% chance
    const changeProbability = moneyness > 0.7 ? 0.10 : moneyness > 0.3 ? 0.07 : 0.04;

    const interval = setInterval(() => {
      // Only apply change based on probability
      if (Math.random() > changeProbability) {
        return;
      }

      setTraderCount(prev => {
        const change = Math.floor(Math.random() * (maxChange - minChange + 1)) + minChange;
        const newCount = prev + change;
        const finalCount = Math.max(1, newCount);
        
        // Detect change direction and trigger color animation
        if (finalCount !== prev) {
          const isIncrease = finalCount > prev;
          const now = Date.now();
          
          // Clear any existing timeout
          if (colorTimeoutRef.current) {
            clearTimeout(colorTimeoutRef.current);
            colorTimeoutRef.current = null;
          }
          
          // Set color immediately (green for increase, red for decrease)
          setTraderCountColor(isIncrease ? brandColors.green : brandColors.red);
          setShouldAnimate(false); // Reset animation flag
          lastColorChangeRef.current = now;
          
          // After 1 second, enable animation and change to grey
          colorTimeoutRef.current = setTimeout(() => {
            setShouldAnimate(true); // Enable animation
            setTraderCountColor('rgba(255, 255, 255, 0.6)');
            colorTimeoutRef.current = null;
          }, 1000);
        }
        
        return finalCount;
      });
    }, intervalMs);

    return () => {
      clearInterval(interval);
      // Don't clear color timeout here - let it complete its animation
      // It will be cleaned up when component unmounts or when a new color change occurs
    };
  }, [moneyness]);

  // Ensure color always animates back to grey after staying for 1 second
  // This handles edge cases where the timeout might not fire
  useEffect(() => {
    // Only set up a timeout if the color is not grey AND we don't already have a timeout running
    if (traderCountColor !== 'rgba(255, 255, 255, 0.6)' && !colorTimeoutRef.current) {
      // Calculate time since last color change
      const timeSinceChange = Date.now() - lastColorChangeRef.current;
      const remainingTime = Math.max(0, 1000 - timeSinceChange);
      
      // Set timeout to start animating back to grey after 1 second total
      // The animation itself takes 1 second (handled by motion.div transition)
      colorTimeoutRef.current = setTimeout(() => {
        setShouldAnimate(true); // Enable animation
        setTraderCountColor('rgba(255, 255, 255, 0.6)');
        colorTimeoutRef.current = null;
      }, remainingTime);
    }
  }, [traderCountColor]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (colorTimeoutRef.current) {
        clearTimeout(colorTimeoutRef.current);
        colorTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.02, 0.3) }}
      className="grid items-center gap-2 px-2 py-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors overflow-x-hidden"
      style={{ gridTemplateColumns: '110px 0.3fr 0.3fr 0.4fr auto', maxWidth: '100%' }}
      onClick={onClick}
    >
      {/* Type indicator + Symbol */}
      <div className="flex items-center gap-2">
        <span
          className="text-xs font-bold uppercase whitespace-nowrap"
          style={{ color: isCall ? brandColors.green : brandColors.red }}
        >
          {isCall ? 'ABOVE' : 'BELOW'}
        </span>
        <div className="text-white font-semibold text-sm whitespace-nowrap">
          ${formatStrikePrice(option.strikePrice)}
        </div>
      </div>

      {/* Leverage */}
      <div className="text-right">
        <div className="text-white/80 font-semibold text-sm whitespace-nowrap">
          {formatLeverage(calculateLeverage(option))}
        </div>
      </div>

      {/* Traders */}
      <div className="text-right">
        <motion.div
          className="font-medium text-sm whitespace-nowrap flex items-center justify-end gap-1.5"
          animate={{ color: traderCountColor }}
          transition={shouldAnimate ? { duration: 1, ease: 'easeOut' } : { duration: 0 }}
        >
          <FontAwesomeIcon icon={faUsers} className="text-xs" />
          {traderCount}
        </motion.div>
      </div>

      {/* Middle: Performance */}
      <div className="text-right">
        <div
          className="font-semibold text-sm whitespace-nowrap"
          style={{ color: isPositive ? brandColors.green : brandColors.red }}
        >
          {formatPercent(option.priceChangePercent)}
        </div>
      </div>

      {/* Right: Arrow icon */}
      <motion.button
        className="p-1 text-white/60 hover:text-white transition-colors cursor-pointer flex-shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.5 15L12.5 10L7.5 5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </motion.button>

      {/* Right: Buy button with price - commented out */}
      {/* <motion.button
        className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex-shrink-0 min-w-[72px] text-center"
        style={{
          backgroundColor: `${buttonColor}15`,
          borderColor: buttonColor,
          borderWidth: 1,
          color: buttonColor,
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleBuyClick}
      >
        {formatPrice(option.currentPrice)}
      </motion.button> */}
    </motion.div>
  );
}
